import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Pressable, Modal, StyleSheet, useWindowDimensions, ScrollView } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { Button, H2, SizableText, View, XStack, YStack } from 'tamagui';
import { HIDE_CALL_MODAL, CHANGE_CALL_STATUS } from '@reduxLocal/actions/types';
import { AntDesign, MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import SocketManager from "@utils/socket";
import apis, { api } from "@api/api";

const CallModalWeb = () => {
  const windowDimensions = useWindowDimensions();
  const modalDisplayed = useSelector((state: DefaultRootState) => state.modals.callModal.modalDisplayed);
  const details = useSelector((state: DefaultRootState) => state.modals.callModal.details);
  const status = useSelector((state: DefaultRootState) => state.modals.callModal.status);
  const theme = themeSelector();
  const dispatch = useDispatch();

  const [timer, setTimer] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle, connecting, connected, error
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [stages, setStages] = useState<any[]>([]);
  const [transcriptionLog, setTranscriptionLog] = useState<Array<{ type: string, text: string, timestamp: number }>>([]);
  const conversationScrollRef = useRef<ScrollView>(null);
  const [transcriptionStatus, setTranscriptionStatus] = useState<'inactive' | 'active' | 'paused'>('inactive');
  const [transcriptionMessage, setTranscriptionMessage] = useState<string>('');

  // Microphone level monitoring
  const [micLevel, setMicLevel] = useState<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const boostedStreamRef = useRef<MediaStream | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (conversationScrollRef.current && transcriptionLog.length > 0) {
      setTimeout(() => {
        conversationScrollRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [transcriptionLog]);

  // Debug: Log when transcription status changes
  useEffect(() => {
    console.log('🎨 UI RE-RENDER: Transcription status changed to:', transcriptionStatus);
    console.log('   Message:', transcriptionMessage);
    console.log('   Expected color:', transcriptionStatus === 'active' ? 'GREEN' : 'ORANGE');
  }, [transcriptionStatus, transcriptionMessage]);

  // Audio device management
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicId, setSelectedMicId] = useState<string>('');
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>('');

  // WebRTC refs
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const socketManagerRef = useRef<SocketManager | null>(null);
  const whisperWebSocketRef = useRef<WebSocket | null>(null);
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);

  // Enumerate audio devices when modal opens
  useEffect(() => {
    if (!modalDisplayed) return;

    const enumerateDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');

        setAudioInputDevices(audioInputs);
        setAudioOutputDevices(audioOutputs);

        // Set default devices
        if (audioInputs.length > 0 && !selectedMicId) {
          setSelectedMicId(audioInputs[0].deviceId);
        }
        if (audioOutputs.length > 0 && !selectedSpeakerId) {
          setSelectedSpeakerId(audioOutputs[0].deviceId);
        }

        console.log('🎤 Audio input devices:', audioInputs.length);
        console.log('🔊 Audio output devices:', audioOutputs.length);
      } catch (error) {
        console.error('❌ Error enumerating devices:', error);
      }
    };

    enumerateDevices();
  }, [modalDisplayed]);

  // Initialize WebSocket and WebRTC when modal opens
  useEffect(() => {
    if (!modalDisplayed) return;

    // Initialize socket connection for backend communication
    socketManagerRef.current = new SocketManager('call', {
      autoconnect: false,
      reconnect: true
    });

    const socket = socketManagerRef.current.getSocket();

    // Connect to Whisper WebSocket for WebRTC signaling
    const connectToWhisperWebSocket = () => {
      const whisperWsUrl = 'ws://localhost:11438/signal';
      console.log('🔌 Connecting to Whisper WebSocket:', whisperWsUrl);

      whisperWebSocketRef.current = new WebSocket(whisperWsUrl);

      whisperWebSocketRef.current.onopen = () => {
        console.log('✅ Connected to Whisper WebSocket');
        console.log('🔌 WebSocket ready state:', whisperWebSocketRef.current?.readyState);
        console.log('🔌 WebSocket URL:', whisperWsUrl);
        console.log('🔌 WebSocket protocol:', whisperWebSocketRef.current?.protocol);
      };

      whisperWebSocketRef.current.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Whisper WebSocket message:', data);

          // Handle WebRTC signaling responses
          if (data.answer) {
            console.log('📝 Received SDP answer from Whisper');

            // Set the remote description (answer) on the peer connection
            if (peerConnectionRef.current) {
              await peerConnectionRef.current.setRemoteDescription(data.answer);
              console.log('✅ Remote description set from Whisper answer');
            }
          }

          // Handle ICE candidates from Whisper
          if (data.iceCandidate) {
            console.log('🧊 Received ICE candidate from Whisper');

            if (peerConnectionRef.current) {
              await peerConnectionRef.current.addIceCandidate(data.iceCandidate);
              console.log('✅ ICE candidate added from Whisper');
            }
          }

          // Handle transcription from Whisper
          if (data.type === 'transcription') {
            console.log('💬 User transcription received:', data.text);
            const newEntry = {
              type: 'user',
              text: data.text,
              timestamp: Date.now()
            };
            console.log('📝 Adding to transcription log:', newEntry);
            setTranscriptionLog(prev => {
              const updated = [...prev, newEntry];
              console.log('📋 Transcription log updated, total entries:', updated.length);
              return updated;
            });
          }

          // Handle AI response from Whisper
          if (data.type === 'ai_response') {
            console.log('🤖 AI response received:', data.text);
            const newEntry = {
              type: 'ai',
              text: data.text,
              timestamp: Date.now()
            };
            console.log('📝 Adding AI response to log:', newEntry);
            setTranscriptionLog(prev => {
              const updated = [...prev, newEntry];
              console.log('📋 Transcription log updated, total entries:', updated.length);
              return updated;
            });
          }

          // Handle stage progression from Whisper
          if (data.type === 'stage_progress') {
            console.log('🔄 Stage progressed to:', data.currentStage + 1, '/', data.totalStages);
            setCurrentStageIndex(data.currentStage);
            if (stages[data.currentStage]) {
              console.log('📍 Now on stage:', stages[data.currentStage].title);
              console.log('   Keywords:', stages[data.currentStage].voiceConfig?.navigationKeywords);
            }
          }

          // Handle transcription status updates
          if (data.type === 'transcription_status') {
            console.log('🎙️ Transcription status UPDATE:', data.status, '-', data.message);
            console.log('   Previous status:', transcriptionStatus);
            console.log('   New status:', data.status);
            setTranscriptionStatus(data.status);
            setTranscriptionMessage(data.message);
            console.log('   ✅ State updated');
          }

          // Handle TTS events (begin/end)
          if (data.type === 'tts_event') {
            console.log('🔊 TTS Event:', data.event, '-', data.segment);
            if (data.event === 'begin') {
              console.log('   🎬 TTS Started:', data.text);
              setTranscriptionStatus('paused');
              setTranscriptionMessage('AI speaking...');
            } else if (data.event === 'end') {
              console.log('   🏁 TTS Ended:', data.segment);
              // Don't set to active here - wait for transcription_status message
            }
          }

        } catch (error) {
          console.error('❌ Error parsing Whisper WebSocket message:', error);
        }
      };

      whisperWebSocketRef.current.onerror = (error) => {
        console.error('❌ Whisper WebSocket error:', error);
        console.error('🔍 WebSocket URL:', whisperWsUrl);
        console.error('🔍 WebSocket ready state:', whisperWebSocketRef.current?.readyState);
      };

      whisperWebSocketRef.current.onclose = (event) => {
        console.log('🔌 Whisper WebSocket disconnected');
        console.log('🔍 Close code:', event.code);
        console.log('🔍 Close reason:', event.reason);
        console.log('🔍 Was clean:', event.wasClean);
        console.log('🔍 WebSocket ready state before close:', whisperWebSocketRef.current?.readyState);

        // Log common close codes for debugging
        if (event.code === 1005) {
          console.log('⚠️ Close code 1005: No status received (connection closed without close frame)');
        } else if (event.code === 1000) {
          console.log('✅ Close code 1000: Normal closure');
        } else if (event.code === 1001) {
          console.log('🚀 Close code 1001: Going away (page navigation)');
        } else if (event.code === 1006) {
          console.log('❌ Close code 1006: Abnormal closure (no close frame)');
        }
      };
    };

    connectToWhisperWebSocket();

    // Setup WebRTC peer connection
    const setupPeerConnection = () => {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });

      // Handle ICE candidates
      pc.onicecandidate = async (event) => {
        if (event.candidate && sessionId && whisperWebSocketRef.current?.readyState === WebSocket.OPEN) {
          console.log('📡 Sending ICE candidate to Whisper WebSocket');

          // Send via Whisper WebSocket for real-time signaling
          const candidateMessage = {
            sessionId: sessionId,
            iceCandidate: event.candidate
          };

          whisperWebSocketRef.current.send(JSON.stringify(candidateMessage));

          // Also send via backend API for logging/fallback
          try {
            await api.post(`/ai/flow/test/webrtc-signal/${sessionId}`, {
              type: 'candidate',
              data: { candidate: event.candidate }
            });
          } catch (error) {
            console.error('Error sending ICE candidate via API:', error);
          }
        } else if (event.candidate && sessionId) {
          console.log('⚠️ Whisper WebSocket not ready, storing candidate for later');
          // Store candidate for when WebSocket becomes available
        }
      };

      // Handle remote audio track
      pc.ontrack = (event) => {
        console.log('🎵 Received remote track:', event.track.kind);
        console.log('🎵 Track state:', event.track.readyState);
        console.log('🎵 Streams:', event.streams);

        if (event.track.kind === 'audio') {
          const remoteStream = new MediaStream([event.track]);
          console.log('🎵 Created remote stream:', remoteStream.id);
          console.log('🎵 Stream active:', remoteStream.active);
          console.log('🎵 Audio tracks:', remoteStream.getAudioTracks().length);

          // Create or update audio element
          if (!remoteAudioRef.current) {
            console.log('🔊 Creating new Audio element');
            remoteAudioRef.current = new Audio();
            remoteAudioRef.current.autoplay = true;
            remoteAudioRef.current.volume = 1.0;

            // Add event listeners for debugging
            remoteAudioRef.current.onplay = () => console.log('✅ Audio playback started');
            remoteAudioRef.current.onpause = () => console.log('⏸️  Audio playback paused');
            remoteAudioRef.current.onerror = (e) => console.error('❌ Audio error:', e);
            remoteAudioRef.current.onloadedmetadata = () => console.log('📊 Audio metadata loaded');
          }

          remoteAudioRef.current.srcObject = remoteStream;
          console.log('✅ Remote audio stream connected to audio element');

          // Set audio output device if selected
          if (selectedSpeakerId && 'setSinkId' in remoteAudioRef.current) {
            (remoteAudioRef.current as any).setSinkId(selectedSpeakerId).then(() => {
              console.log('🔊 Audio output device set:', selectedSpeakerId);
            }).catch((err: any) => {
              console.error('❌ Failed to set audio output device:', err);
            });
          }

          // Force play in case autoplay is blocked
          remoteAudioRef.current.play().then(() => {
            console.log('✅ Audio play() succeeded');
          }).catch(err => {
            console.error('❌ Audio play() failed:', err);
            console.log('💡 Click anywhere to enable audio playback');
          });
        }
      };

      // Handle connection state changes
      pc.onconnectionstatechange = () => {
        console.log('Connection state:', pc.connectionState);
        setCallStatus(pc.connectionState);

        if (pc.connectionState === 'connected') {
          setIsConnected(true);
          dispatch({ type: CHANGE_CALL_STATUS, payload: 'Connected' });

          // Stop ringtone when connected
          if (ringtoneRef.current) {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
            console.log('🔕 Ringtone stopped');
          }
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setIsConnected(false);

          // Stop ringtone on failure/disconnect too
          if (ringtoneRef.current) {
            ringtoneRef.current.pause();
            ringtoneRef.current.currentTime = 0;
          }
        }
      };

      peerConnectionRef.current = pc;
      return pc;
    };

    // Handle incoming SDP answer
    const handleAnswer = async (data) => {
      try {
        const sdp = JSON.parse(data);
        const remoteDesc = new RTCSessionDescription({
          sdp: sdp.sdp,
          type: sdp.type
        });

        if (peerConnectionRef.current) {
          await peerConnectionRef.current.setRemoteDescription(remoteDesc);
          console.log('Remote description set successfully');
          setCallStatus('connected');
        }
      } catch (error) {
        console.error('Error setting remote description:', error);
        setCallStatus('error');
      }
    };

    // Handle incoming ICE candidates
    const handleIceCandidate = async (data) => {
      try {
        const { candidate } = data;
        if (peerConnectionRef.current && candidate) {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          console.log('ICE candidate added');
        }
      } catch (error) {
        console.error('Error adding ICE candidate:', error);
      }
    };

    // Handle transcription events (for flow responses)
    const handleTranscription = async (data) => {
      console.log('📝 Transcription received:', data);

      // Progress to next stage via API
      if (sessionId && details?.flowId) {
        try {
          console.log('➡️ Progressing to next stage via API...');
          const progressResponse = await api.post(
            `/ai/flow/test/progress/${details.flowId}/${sessionId}`,
            {
              currentStageIndex: currentStageIndex,
              transcription: data.text || data.transcription,
              userInput: null
            }
          );

          if (progressResponse.data.success) {
            const { nextStage, aiResponse, flowComplete } = progressResponse.data.data;
            console.log('✅ Stage progressed. AI response:', aiResponse);

            if (nextStage) {
              setCurrentStageIndex(nextStage.index);
              console.log('📍 Moving to stage:', nextStage.title);
            }

            if (flowComplete) {
              console.log('🎉 Flow completed!');
              // Could auto-end call or show completion message
            }
          }
        } catch (error) {
          console.error('❌ Error progressing stage:', error);
        }
      }
    };

    // Setup event listeners
    socket.on('answer', handleAnswer);
    socket.on('iceCandidate', handleIceCandidate);
    socket.on('transcription', handleTranscription);

    // Join the call room
    socket.emit('join', {
      userId: details?.handle || 'test-user',
      flowId: details?.flowId,
      flowData: details?.flowData
    });

    // Initialize peer connection
    setupPeerConnection();

    // Cleanup
    return () => {
      socket.off('answer', handleAnswer);
      socket.off('iceCandidate', handleIceCandidate);
      socket.off('transcription', handleTranscription);

      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }

      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current = null;
      }

      if (whisperWebSocketRef.current) {
        whisperWebSocketRef.current.close();
        whisperWebSocketRef.current = null;
      }

      if (socketManagerRef.current) {
        socketManagerRef.current.getSocket().close();
        socketManagerRef.current = null;
      }
    };
  }, [modalDisplayed, details, dispatch]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (modalDisplayed && isConnected) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    } else {
      setTimer(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [modalDisplayed, isConnected]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleStartTest = async () => {
    try {
      console.log('🎬 Starting test with flowId:', details?.flowId);
      setCallStatus('connecting');

      // Play ringtone during connecting phase
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio(require('@assets/sound/phone-ringing.mp3'));
        ringtoneRef.current.loop = true;
        ringtoneRef.current.volume = 0.5;
      }

      ringtoneRef.current.play().catch(err => {
        console.log('Could not play ringtone:', err);
      });

      // Wait for Whisper WebSocket to be ready
      console.log('🔌 Checking Whisper WebSocket connection...');
      if (!whisperWebSocketRef.current || whisperWebSocketRef.current.readyState !== WebSocket.OPEN) {
        console.log('⏳ Waiting for Whisper WebSocket connection...');
        setCallStatus('connecting');

        // Wait up to 5 seconds for WebSocket connection
        let attempts = 0;
        while ((!whisperWebSocketRef.current || whisperWebSocketRef.current.readyState !== WebSocket.OPEN) && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        if (!whisperWebSocketRef.current || whisperWebSocketRef.current.readyState !== WebSocket.OPEN) {
          throw new Error('Whisper WebSocket connection not available. Please check if the Whisper service is running on localhost:11438');
        }
      }
      console.log('✅ Whisper WebSocket is ready');

      // Step 1: Initialize flow test session via REST API
      console.log('📡 Initializing flow test session via API...');
      const initResponse = await api.post(
        `/ai/flow/test/initialize/${details?.flowId}`,
        {
          userId: details?.flowData?.userId || details?.flowData?.createdBy?._id,
          flowData: details?.flowData
        }
      );

      if (!initResponse.data.success) {
        throw new Error('Failed to initialize flow test session');
      }

      const newSessionId = initResponse.data.data.sessionId;
      setSessionId(newSessionId);
      console.log('✅ Session initialized:', newSessionId);

      // Store flow stages for UI
      if (initResponse.data.data.flowContext?.stages) {
        const loadedStages = initResponse.data.data.flowContext.stages;
        setStages(loadedStages);
        console.log('📋 Loaded stages:', loadedStages.length);
        console.log('📋 Stage details:', loadedStages.map((s, i) => ({
          index: i,
          title: s.title,
          hasAiPrompt: !!s.voiceConfig?.aiPrompt,
          keywordsCount: s.voiceConfig?.navigationKeywords?.length || 0
        })));
      } else {
        console.warn('⚠️  No stages found in flowContext!');
        console.log('flowContext:', initResponse.data.data.flowContext);
      }

      // Step 2: Get user media (microphone)
      console.log('🎤 Requesting microphone access...');
      console.log('🎤 Selected microphone:', selectedMicId || 'default');

      const audioConstraints: any = {
        echoCancellation: true,
        noiseSuppression: false,  // Disable to preserve our boosted audio
        autoGainControl: false    // DISABLE - this was reducing our boost!
      };

      // Use selected device if available
      if (selectedMicId) {
        audioConstraints.deviceId = { exact: selectedMicId };
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: false
      });

      localStreamRef.current = stream;
      console.log('✅ Local stream obtained');

      // Start monitoring microphone level (use original stream for accurate monitoring)
      startMicMonitoring(stream);

      // Boost audio for WebRTC transmission
      const boostedStream = boostAudioStream(stream);

      // Step 3: Setup WebRTC connection
      if (peerConnectionRef.current) {
        // Use BOOSTED stream for WebRTC
        boostedStream.getAudioTracks().forEach(track => {
          console.log('➕ Adding BOOSTED audio track to peer connection');
          peerConnectionRef.current?.addTrack(track, boostedStream);
        });

        // Create and send offer
        const offer = await peerConnectionRef.current.createOffer({
          offerToReceiveAudio: true
        });

        await peerConnectionRef.current.setLocalDescription(offer);
        console.log('📤 Local description set, sending offer');

        // Send offer directly to Whisper WebSocket with flowContext
        const offerMessage = {
          sessionId: newSessionId,
          flowContext: initResponse.data.data.flowContext,
          offer: {
            sdp: offer.sdp,
            type: offer.type
          }
        };

        console.log('📤 Sending WebRTC offer to Whisper WebSocket');
        console.log('🔍 WebSocket ready state:', whisperWebSocketRef.current?.readyState);
        console.log('🔍 WebSocket URL:', whisperWebSocketRef.current?.url);
        console.log('📋 Flow Context:', initResponse.data.data.flowContext?.title);

        if (whisperWebSocketRef.current?.readyState === WebSocket.OPEN) {
          whisperWebSocketRef.current.send(JSON.stringify(offerMessage));
          console.log('✅ WebRTC offer sent to Whisper service with flow context');
        } else {
          console.error('❌ Cannot send offer: WebSocket not ready');
          console.error('🔍 Ready state:', whisperWebSocketRef.current?.readyState);
          throw new Error('WebSocket connection not ready');
        }

        console.log('🔄 WebRTC signaling initiated');
      }
    } catch (error) {
      console.error('❌ Error starting test:', error);
      setCallStatus('error');

      // Stop ringtone on error
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }

      // Show user-friendly error
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Microphone access denied. Please allow microphone access to test the flow.');
      } else if (error.name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone to test the flow.');
      } else {
        alert(`Failed to start test: ${error.message || 'Please check your microphone settings.'}`);
      }
    }
  };

  const handleEndCall = useCallback(async () => {
    console.log('🛑 Ending call and cleaning up...');

    // Stop microphone monitoring
    stopMicMonitoring();

    // Cleanup boosted stream
    if (boostedStreamRef.current) {
      boostedStreamRef.current.getTracks().forEach(track => track.stop());
      boostedStreamRef.current = null;
    }
    gainNodeRef.current = null;

    // Stop ringtone if playing
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current = null;
      console.log('🔕 Ringtone stopped and cleaned up');
    }

    // Complete flow test session via API if session exists
    if (sessionId && details?.flowId) {
      try {
        console.log('📡 Completing flow test session via API...');
        await api.post(
          `/ai/flow/test/complete/${details.flowId}/${sessionId}`,
          {
            completionTime: Math.round(timer / 60), // Convert seconds to minutes
            stagesCompleted: currentStageIndex,
            feedback: null
          }
        );
        console.log('✅ Flow test session completed');
      } catch (error) {
        console.error('❌ Error completing flow test:', error);
      }
    }

    // Stop all audio tracks
    if (localStreamRef.current) {
      console.log('🎤 Stopping local audio tracks');
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Stopping track:', track.kind, track.label);
        track.stop();
        track.enabled = false;
      });
      localStreamRef.current = null;
    }

    // Stop remote audio
    if (remoteAudioRef.current) {
      console.log('🔊 Stopping remote audio');
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      console.log('🔌 Closing peer connection');
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Notify server via WebSocket
    const socket = socketManagerRef.current?.getSocket();
    if (socket) {
      console.log('📡 Notifying server of call end via WebSocket');
      socket.emit('endCall', { sessionId });
    }

    // Reset state
    setIsConnected(false);
    setTimer(0);
    setCallStatus('idle');
    setIsMuted(false);
    setIsSpeakerOn(false);
    setSessionId(null);
    setCurrentStageIndex(0);

    dispatch({ type: HIDE_CALL_MODAL });
    console.log('✅ Call ended and modal closed');
  }, [dispatch, sessionId, details?.flowId, timer, currentStageIndex]);

  const toggleMute = () => {
    if (localStreamRef.current) {
      const newMutedState = !isMuted;
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !newMutedState; // If muting, disable track; if unmuting, enable track
      });
      setIsMuted(newMutedState);
      console.log('🎤 Microphone', newMutedState ? 'muted' : 'unmuted');
    }
  };

  const toggleSpeaker = () => {
    // On web, this would control the output device
    // For now, just toggle the state
    setIsSpeakerOn(!isSpeakerOn);
    console.log('Speaker toggle:', !isSpeakerOn);
  };

  // Boost audio gain on the frontend before sending to WebRTC
  const boostAudioStream = useCallback((stream: MediaStream): MediaStream => {
    try {
      console.log('🔊 Boosting audio stream on frontend...');

      // Check ORIGINAL stream sample rate
      const originalTrack = stream.getAudioTracks()[0];
      if (originalTrack) {
        const settings = originalTrack.getSettings();
        console.log('🎤 ORIGINAL mic stream settings:', settings);
        console.log(`   Sample rate: ${settings.sampleRate}Hz`);
        console.log(`   Channel count: ${settings.channelCount}`);
      }

      // Create audio context WITHOUT forcing sample rate - let it match the input
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log(`🎵 Audio context created with sample rate: ${audioContext.sampleRate}Hz`);

      const source = audioContext.createMediaStreamSource(stream);

      // Create gain node with moderate boost (8x)
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 8.0; // Balanced for VAD + transcription

      // Create destination for the boosted stream
      const destination = audioContext.createMediaStreamDestination();

      // Connect: source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(destination);

      // Store refs for cleanup
      gainNodeRef.current = gainNode;
      boostedStreamRef.current = destination.stream;

      console.log(`✅ Audio boosted ${gainNode.gain.value}x on frontend`);
      console.log('   Original stream tracks:', stream.getAudioTracks().length);
      console.log('   Boosted stream tracks:', destination.stream.getAudioTracks().length);

      // Log the track settings
      const boostedTrack = destination.stream.getAudioTracks()[0];
      if (boostedTrack) {
        const boostedSettings = boostedTrack.getSettings();
        console.log('   Boosted track settings:', boostedSettings);
        console.log(`   Boosted sample rate: ${boostedSettings.sampleRate}Hz`);
      }

      return destination.stream;
    } catch (error) {
      console.error('❌ Error boosting audio:', error);
      return stream; // Return original if boost fails
    }
  }, []);

  // Start monitoring microphone level
  const startMicMonitoring = useCallback((stream: MediaStream) => {
    try {
      // Create audio context and analyser
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start analyzing
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average level
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const normalizedLevel = average / 255; // 0 to 1

        setMicLevel(normalizedLevel);
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
      console.log('🎤 Microphone monitoring started');
    } catch (error) {
      console.error('❌ Error starting mic monitoring:', error);
    }
  }, []);

  // Stop monitoring microphone level
  const stopMicMonitoring = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setMicLevel(0);
    console.log('🎤 Microphone monitoring stopped');
  }, []);

  const handleMicChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceId = event.target.value;
    setSelectedMicId(deviceId);
    console.log('🎤 Microphone changed to:', deviceId);

    // If currently in a call, restart the audio stream with new device
    if (isConnected && localStreamRef.current) {
      try {
        // Stop monitoring on old stream
        stopMicMonitoring();

        // Stop current tracks
        localStreamRef.current.getTracks().forEach(track => track.stop());

        // Get new stream with selected device
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: deviceId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        localStreamRef.current = stream;

        // Start monitoring new stream
        startMicMonitoring(stream);

        // Replace tracks in peer connection
        if (peerConnectionRef.current) {
          const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'audio');
          if (sender) {
            await sender.replaceTrack(stream.getAudioTracks()[0]);
            console.log('✅ Microphone track replaced');
          }
        }
      } catch (error) {
        console.error('❌ Error changing microphone:', error);
      }
    }
  };

  const handleSpeakerChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const deviceId = event.target.value;
    setSelectedSpeakerId(deviceId);
    console.log('🔊 Speaker changed to:', deviceId);

    // Change audio output device
    if (remoteAudioRef.current && 'setSinkId' in remoteAudioRef.current) {
      try {
        await (remoteAudioRef.current as any).setSinkId(deviceId);
        console.log('✅ Speaker device set successfully');
      } catch (error) {
        console.error('❌ Error setting speaker device:', error);
      }
    }
  };

  // Handle ESC key press
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalDisplayed) {
        handleEndCall();
      }
    };

    if (typeof window !== 'undefined' && modalDisplayed) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [modalDisplayed, handleEndCall]);

  if (!modalDisplayed) return null;

  return (
    <Modal
      visible={modalDisplayed}
      transparent={true}
      animationType="fade"
      onRequestClose={handleEndCall}
    >
      <Pressable
        style={styles.overlay}
        onPress={handleEndCall}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{ maxWidth: 500, width: '100%' }}
        >
          <Animatable.View
            animation="zoomIn"
            duration={300}
            style={[
              styles.modalContent,
              {
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderColor: theme ? '#e2e8f0' : '#334155'
              }
            ]}
          >
            {/* Close Button */}
            <View style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10
            }}>
              <Button
                size="$3"
                circular
                backgroundColor={theme ? '#f1f5f9' : '#334155'}
                hoverStyle={{ backgroundColor: theme ? '#e2e8f0' : '#475569' }}
                pressStyle={{ backgroundColor: theme ? '#cbd5e1' : '#64748b' }}
                onPress={handleEndCall}
                icon={<AntDesign name="close" size={18} color={theme ? '#64748b' : '#94a3b8'} />}
                padding={8}
              />
            </View>

            {/* Header */}
            <YStack gap="$4" alignItems="center" padding="$6">
              <AvatarHandler
                avatar={details?.avatar}
                avatarImageStyle={{
                  borderRadius: 80,
                  height: 140,
                  width: 140,
                  borderWidth: 4,
                  borderColor: isConnected ? '#10b981' : '#6b7280'
                }}
              />

              <YStack alignItems="center" gap="$2">
                <H2 style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 28,
                  fontWeight: '700'
                }}>
                  {details?.handle || 'Test Flow'}
                </H2>

                {status === 'testing' && (
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    backgroundColor: theme ? '#fef3c7' : '#78350f',
                    borderRadius: 12
                  }}>
                    <MaterialCommunityIcons
                      name="flask"
                      size={18}
                      color={theme ? '#d97706' : '#fbbf24'}
                    />
                    <SizableText style={{
                      color: theme ? '#d97706' : '#fbbf24',
                      fontWeight: '600',
                      fontSize: 14
                    }}>
                      Test Mode
                    </SizableText>
                  </View>
                )}
              </YStack>

              {/* Status Display */}
              <View style={[
                styles.statusContainer,
                {
                  backgroundColor: theme ? '#f1f5f9' : '#334155'
                }
              ]}>
                <View style={{ alignItems: 'center', gap: 8 }}>
                  {callStatus === 'connecting' ? (
                    <>
                      <MaterialCommunityIcons
                        name="dots-horizontal-circle"
                        size={48}
                        color={theme ? '#f59e0b' : '#fbbf24'}
                      />
                      <SizableText style={{
                        color: theme ? '#f59e0b' : '#fbbf24',
                        fontSize: 16,
                        fontWeight: '600'
                      }}>
                        Connecting...
                      </SizableText>
                    </>
                  ) : isConnected ? (
                    <>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <View style={[
                          styles.statusDot,
                          { backgroundColor: '#10b981' }
                        ]} />
                        <SizableText style={{
                          color: theme ? '#1e293b' : '#f1f5f9',
                          fontSize: 16,
                          fontWeight: '600'
                        }}>
                          {isMuted ? 'Muted' : 'Testing Active'}
                        </SizableText>
                      </View>
                      <SizableText style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 24,
                        fontWeight: '700',
                        fontVariant: ['tabular-nums']
                      }}>
                        {formatTime(timer)}
                      </SizableText>
                    </>
                  ) : callStatus === 'error' ? (
                    <>
                      <MaterialIcons
                        name="error-outline"
                        size={48}
                        color="#ef4444"
                      />
                      <SizableText style={{
                        color: '#ef4444',
                        fontSize: 16,
                        fontWeight: '600'
                      }}>
                        Connection Failed
                      </SizableText>
                    </>
                  ) : (
                    <>
                      <MaterialIcons
                        name="play-circle-outline"
                        size={48}
                        color={theme ? '#3b82f6' : '#60a5fa'}
                      />
                      <SizableText style={{
                        color: theme ? '#64748b' : '#94a3b8',
                        fontSize: 16,
                        fontWeight: '500'
                      }}>
                        Ready to test
                      </SizableText>
                    </>
                  )}
                </View>
              </View>
            </YStack>

            {/* Action Buttons */}
            {!isConnected && callStatus !== 'connecting' ? (
              <XStack gap="$3" padding="$4" paddingTop="$2">
                <Button
                  flex={1}
                  size="$5"
                  backgroundColor="#10b981"
                  hoverStyle={{ backgroundColor: '#059669' }}
                  pressStyle={{ backgroundColor: '#047857' }}
                  onPress={handleStartTest}
                  icon={<MaterialIcons name="play-arrow" size={24} color="white" />}
                >
                  <SizableText style={styles.buttonText}>
                    Start Test
                  </SizableText>
                </Button>

                <Button
                  flex={1}
                  size="$5"
                  backgroundColor="#6b7280"
                  hoverStyle={{ backgroundColor: '#4b5563' }}
                  pressStyle={{ backgroundColor: '#374151' }}
                  onPress={handleEndCall}
                  icon={<AntDesign name="close" size={24} color="white" />}
                >
                  <SizableText style={styles.buttonText}>
                    Cancel
                  </SizableText>
                </Button>
              </XStack>
            ) : callStatus === 'connecting' ? (
              <View style={{ padding: 16, alignItems: 'center' }}>
                <SizableText style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14
                }}>
                  Establishing connection...
                </SizableText>
              </View>
            ) : (
              <YStack gap="$3" padding="$4" paddingTop="$2">
                {/* Device Selection Dropdowns */}
                <YStack gap="$2">
                  {/* Microphone Selection */}
                  <View style={{ gap: 4 }}>
                    <SizableText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 12,
                      fontWeight: '600',
                      paddingLeft: 4
                    }}>
                      Microphone
                    </SizableText>
                    <select
                      value={selectedMicId}
                      onChange={handleMicChange}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      {audioInputDevices.map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>

                    {/* Microphone Level Monitor */}
                    <View style={{
                      marginTop: 8,
                      gap: 4
                    }}>
                      <XStack style={{
                        alignItems: 'center',
                        gap: 6
                      }}>
                        <SizableText style={{
                          color: theme ? '#64748b' : '#94a3b8',
                          fontSize: 11,
                          fontWeight: '600'
                        }}>
                          INPUT LEVEL
                        </SizableText>
                        <SizableText style={{
                          color: micLevel > 0.7 ? '#22c55e' : micLevel > 0.3 ? '#f59e0b' : '#6b7280',
                          fontSize: 10,
                          fontWeight: '700',
                          fontFamily: 'monospace'
                        }}>
                          {Math.round(micLevel * 100)}%
                        </SizableText>
                      </XStack>
                      <View style={{
                        height: 6,
                        backgroundColor: theme ? '#e2e8f0' : '#334155',
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <View style={{
                          width: `${micLevel * 100}%`,
                          height: '100%',
                          backgroundColor: micLevel > 0.7 ? '#22c55e' : micLevel > 0.3 ? '#f59e0b' : '#6b7280',
                          transition: 'width 0.1s ease-out, background-color 0.2s'
                        }} />
                      </View>
                      {micLevel < 0.05 && isConnected && (
                        <SizableText style={{
                          color: '#ef4444',
                          fontSize: 10,
                          fontWeight: '600',
                          marginTop: 2
                        }}>
                          ⚠️ Very low input - check microphone or speak louder
                        </SizableText>
                      )}
                    </View>
                  </View>

                  {/* Speaker Selection */}
                  <View style={{ gap: 4 }}>
                    <SizableText style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 12,
                      fontWeight: '600',
                      paddingLeft: 4
                    }}>
                      Speaker
                    </SizableText>
                    <select
                      value={selectedSpeakerId}
                      onChange={handleSpeakerChange}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${theme ? '#e2e8f0' : '#475569'}`,
                        backgroundColor: theme ? '#f8fafc' : '#1e293b',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      {audioOutputDevices.map(device => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                        </option>
                      ))}
                    </select>
                  </View>
                </YStack>

                {/* Transcription Status Indicator */}
                {transcriptionStatus !== 'inactive' && (
                  <View
                    key={transcriptionStatus}
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: transcriptionStatus === 'active'
                        ? (theme ? '#dcfce7' : '#064e3b')
                        : (theme ? '#fef3c7' : '#78350f'),
                      marginBottom: 12,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      borderWidth: 2,
                      borderColor: transcriptionStatus === 'active' ? '#22c55e' : '#f59e0b'
                    }}>
                    <View style={{
                      width: 12,
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: transcriptionStatus === 'active' ? '#22c55e' : '#f59e0b'
                    }} />
                    <SizableText style={{
                      fontSize: 14,
                      fontWeight: '700',
                      color: transcriptionStatus === 'active'
                        ? (theme ? '#166534' : '#bbf7d0')
                        : (theme ? '#92400e' : '#fde68a')
                    }}>
                      {transcriptionStatus === 'active' ? '🎤 LISTENING' : '⏸️ AI SPEAKING'}
                    </SizableText>
                    {transcriptionMessage && (
                      <SizableText style={{
                        fontSize: 12,
                        fontWeight: '600',
                        color: transcriptionStatus === 'active'
                          ? (theme ? '#15803d' : '#86efac')
                          : (theme ? '#a16207' : '#fcd34d')
                      }}>
                        {transcriptionMessage}
                      </SizableText>
                    )}
                    {/* Debug display */}
                    <SizableText style={{
                      fontSize: 9,
                      color: theme ? '#64748b' : '#94a3b8',
                      marginLeft: 'auto'
                    }}>
                      [{transcriptionStatus}]
                    </SizableText>
                  </View>
                )}

                {/* Call Controls Row */}
                <XStack gap="$3">
                  <Button
                    flex={1}
                    size="$4"
                    backgroundColor={isMuted ? '#ef4444' : '#6b7280'}
                    hoverStyle={{ backgroundColor: isMuted ? '#dc2626' : '#4b5563' }}
                    pressStyle={{ backgroundColor: isMuted ? '#b91c1c' : '#374151' }}
                    onPress={toggleMute}
                    icon={<MaterialIcons name={isMuted ? 'mic-off' : 'mic'} size={20} color="white" />}
                  >
                    <SizableText style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                      {isMuted ? 'Unmute' : 'Mute'}
                    </SizableText>
                  </Button>

                  <Button
                    flex={1}
                    size="$4"
                    backgroundColor={isSpeakerOn ? '#3b82f6' : '#6b7280'}
                    hoverStyle={{ backgroundColor: isSpeakerOn ? '#2563eb' : '#4b5563' }}
                    pressStyle={{ backgroundColor: isSpeakerOn ? '#1d4ed8' : '#374151' }}
                    onPress={toggleSpeaker}
                    icon={<MaterialIcons name={isSpeakerOn ? 'volume-up' : 'volume-down'} size={20} color="white" />}
                  >
                    <SizableText style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                      Speaker
                    </SizableText>
                  </Button>
                </XStack>

                {/* End Call Button */}
                <Button
                  size="$5"
                  backgroundColor="#ef4444"
                  hoverStyle={{ backgroundColor: '#dc2626' }}
                  pressStyle={{ backgroundColor: '#b91c1c' }}
                  onPress={handleEndCall}
                  icon={<MaterialIcons name="call-end" size={24} color="white" />}
                >
                  <SizableText style={styles.buttonText}>
                    End Test
                  </SizableText>
                </Button>
              </YStack>
            )}

            {/* Flow Info and Transcription Log */}
            {details?.flowData && (
              <View style={[
                styles.flowInfo,
                {
                  backgroundColor: theme ? '#f8fafc' : '#0f172a',
                  borderTopColor: theme ? '#e2e8f0' : '#334155'
                }
              ]}>
                <SizableText style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 13,
                  fontWeight: '600',
                  marginBottom: 8
                }}>
                  Flow Information
                </SizableText>
                <View style={{ gap: 6, marginBottom: 12 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <SizableText style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 12 }}>
                      Stage Progress:
                    </SizableText>
                    <SizableText style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 12, fontWeight: '600' }}>
                      {currentStageIndex + 1} / {stages.length || details.flowData.actions?.length || 0}
                    </SizableText>
                  </View>
                  {stages.length > 0 && stages[currentStageIndex] && (
                    <View
                      key={`stage-${currentStageIndex}`}
                      style={{
                        marginTop: 4,
                        padding: 8,
                        borderRadius: 6,
                        backgroundColor: theme ? '#e0f2fe' : '#0c4a6e'
                      }}>
                      <SizableText style={{
                        color: theme ? '#0369a1' : '#7dd3fc',
                        fontSize: 13,
                        fontWeight: '600',
                        marginBottom: 2
                      }}>
                        📍 {stages[currentStageIndex].title}
                      </SizableText>
                      <SizableText style={{
                        color: theme ? '#0c4a6e' : '#bae6fd',
                        fontSize: 11
                      }}>
                        {stages[currentStageIndex].description}
                      </SizableText>
                      {stages[currentStageIndex].voiceConfig?.navigationKeywords?.length > 0 && (
                        <SizableText style={{
                          color: theme ? '#0369a1' : '#7dd3fc',
                          fontSize: 10,
                          marginTop: 4,
                          fontStyle: 'italic'
                        }}>
                          💬 Say: {stages[currentStageIndex].voiceConfig.navigationKeywords.slice(0, 3).join(', ')}
                        </SizableText>
                      )}
                    </View>
                  )}
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                    <SizableText style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 12 }}>
                      Flow Type:
                    </SizableText>
                    <SizableText style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 12, fontWeight: '600', textTransform: 'capitalize' }}>
                      {details.flowData.flowType || 'general'}
                    </SizableText>
                  </View>
                </View>

                {/* Transcription Log */}
                <>
                  <SizableText style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 13,
                    fontWeight: '600',
                    marginBottom: 8,
                    marginTop: 8
                  }}>
                    Conversation {transcriptionLog.length > 0 && `(${transcriptionLog.length})`}
                  </SizableText>
                  <ScrollView
                    ref={conversationScrollRef}
                    style={{
                      maxHeight: 200,
                      minHeight: 60,
                    }}
                    contentContainerStyle={{
                      gap: 8,
                      paddingRight: 4,
                      paddingBottom: 4
                    }}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                  >
                    {transcriptionLog.length === 0 ? (
                      <SizableText style={{
                        fontSize: 12,
                        color: theme ? '#94a3b8' : '#64748b',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        paddingVertical: 20
                      }}>
                        Waiting for conversation to start...
                      </SizableText>
                    ) : (
                      transcriptionLog.map((log, idx) => (
                        <View key={idx} style={{
                          padding: 8,
                          borderRadius: 8,
                          backgroundColor: (log as any).isSystemMessage
                            ? (theme ? '#fef3c7' : '#78350f')
                            : log.type === 'user'
                              ? (theme ? '#dbeafe' : '#1e3a8a')
                              : (theme ? '#f3f4f6' : '#374151'),
                          marginBottom: idx < transcriptionLog.length - 1 ? 8 : 0,
                          borderWidth: (log as any).isSystemMessage ? 1 : 0,
                          borderColor: (log as any).isSystemMessage ? (theme ? '#f59e0b' : '#fbbf24') : 'transparent'
                        }}>
                          <SizableText style={{
                            fontSize: 11,
                            fontWeight: '600',
                            color: (log as any).isSystemMessage
                              ? (theme ? '#92400e' : '#fde68a')
                              : log.type === 'user'
                                ? (theme ? '#1e40af' : '#93c5fd')
                                : (theme ? '#6b7280' : '#9ca3af'),
                            marginBottom: 4
                          }}>
                            {(log as any).isSystemMessage ? '⚠️ System' : log.type === 'user' ? '👤 You' : '🤖 AI'}
                          </SizableText>
                          <SizableText style={{
                            fontSize: 12,
                            color: (log as any).isSystemMessage
                              ? (theme ? '#78350f' : '#fde68a')
                              : log.type === 'user'
                                ? (theme ? '#1e3a8a' : '#bfdbfe')
                                : (theme ? '#1f2937' : '#d1d5db'),
                            fontStyle: (log as any).isSystemMessage ? 'italic' : 'normal'
                          }}>
                            {log.text}
                          </SizableText>
                        </View>
                      ))
                    )}
                  </ScrollView>
                </>
              </View>
            )}
          </Animatable.View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20
  },
  modalContent: {
    borderRadius: 24,
    borderWidth: 1,
    minWidth: 420,
    maxWidth: 500,
    width: '100%',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 20 },
    shadowOpacity: 0.25,
    shadowRadius: 40,
    elevation: 24
  },
  statusContainer: {
    width: '100%',
    padding: 24,
    borderRadius: 16,
    alignItems: 'center'
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    shadowColor: '#10b981',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 8
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '700',
  },
  flowInfo: {
    padding: 16,
    borderTopWidth: 1,
  }
});

export default CallModalWeb;

